import React from 'react';

import { faBrain } from '@fortawesome/pro-regular-svg-icons/faBrain';
import { faCalendar } from '@fortawesome/pro-regular-svg-icons/faCalendar';
import { faCircleCheck } from '@fortawesome/pro-regular-svg-icons/faCircleCheck';

import FontAwesomeIcon from 'components/Common/FontAwesomeIcon';
import Api from 'core/api';
import getStudentTagSourceFilter from 'selectors/filters/getStudentTagSourceFilter';

import { ensureArray } from 'utils/arrays';

import {
  FILTER_SCHOOLS,
  FILTER_GRADES_6_THROUGH_12_ONLY,
  FILTER_STUDENT_TAGS,
} from 'modules/StudentScopeFilter/constants';
import { formatIdFilters } from 'modules/StudentScopeFilter/filterSelectors';

import v from 'components/variables';

export const CHART_COLORS = [
  v.purpleMedium,
  v.yellowMedium,
  v.indigoMedium,
  v.orangeMedium,
  v.violetMedium,
  v.pinkMedium,
  v.blueMedium,
  v.tealMedium,
  v.redMedium,
  v.greenMedium,
];

export const HOVER_CHART_COLORS = Object.freeze({
  [v.purpleMedium]: v.purple,
  [v.yellowMedium]: v.yellow,
  [v.indigoMedium]: v.indigo,
  [v.orangeMedium]: v.orange,
  [v.violetMedium]: v.violet,
  [v.pinkMedium]: v.pink,
  [v.blueMedium]: v.blue,
  [v.tealMedium]: v.teal,
  [v.redMedium]: v.red,
  [v.greenMedium]: v.green,
});

export const FILTER_LIST = [
  FILTER_SCHOOLS,
  FILTER_GRADES_6_THROUGH_12_ONLY,
  FILTER_STUDENT_TAGS,
];

export const FILTER_ALUMNI_K12_SCHOOL = 'k12_school_pk';
export const FILTER_ALUMNI_CLASS_YEAR = 'class_year_pk';

export const ALUMNI_FILTER_LIST = [
  FILTER_ALUMNI_K12_SCHOOL,
  FILTER_ALUMNI_CLASS_YEAR,
];

export const ALUMNI_FILTER_TYPES = {
  [FILTER_ALUMNI_K12_SCHOOL]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.k12_school_pk ?? {},
    queryParamKey: FILTER_ALUMNI_K12_SCHOOL,
    getFilterContext: getStudentTagSourceFilter,
  },
  [FILTER_ALUMNI_CLASS_YEAR]: {
    getQueryParamValue: formatIdFilters,
    getDataFromState: selected => selected?.class_year_pk ?? {},
    queryParamKey: FILTER_ALUMNI_CLASS_YEAR,
    getFilterContext: getStudentTagSourceFilter,
  },
};

export const SURVEY_ANALYTICS_QUERY_LIMIT = 20;

export const CASEFILE_SURVEY_BUTTON_MAP = Object.freeze({
  enabled: 'View response',
  partiallyEnabled: 'View partial response',
  disabled: 'No response',
});

export const SURVEY_ANALYTICS_MODAL = Object.freeze({
  STUDENT_LIST_DRAWER: 'SURVEY_ANALYTICS/STUDENT_LIST_DRAWER',
  TEXT_QUESTION_DETAIL_MODAL: 'SURVEY_ANALYTICS/TEXT_QUESTION_DETAIL_MODAL',
  OTHER_ANSWER_LIST_MODAL: 'SURVEY_ANALYTICS/OTHER_ANSWER_LIST_MODAL',
  VIDEO_RESPONSE_MODAL: 'SURVEY_ANALYTICS/VIDEO_RESPONSE_MODAL',
});
export const VIDEO_RESPONSES_SORT_OPTIONS = [
  {
    title: 'Longest - Shortest',
    orderBy: 'duration',
    value: 'duration',
    order: 'desc',
  },
  { title: 'A - Z', orderBy: 'name', value: 'name', order: 'asc' },
  { title: 'Z - A', orderBy: 'name', value: '-name', order: 'desc' },
];

export const LEARNING_TASKS_TAB_ID = 1;
export const STAFF_FORMS_TAB_ID = 2;
export const ACTIVITIES_TAB_ID = 3;
export const EVENTS_TAB_ID = 4;

export const TABS = [
  {
    label: 'Learning Tasks',
    id: LEARNING_TASKS_TAB_ID,
    icon: <FontAwesomeIcon icon={faBrain} />,
  },
  {
    label: 'Staff Indicators',
    id: STAFF_FORMS_TAB_ID,
    icon: <FontAwesomeIcon icon={faCircleCheck} />,
  },
  {
    label: 'Student Indicators',
    id: ACTIVITIES_TAB_ID,
    icon: <FontAwesomeIcon icon={faCircleCheck} />,
  },
  {
    label: 'Events',
    id: EVENTS_TAB_ID,
    icon: <FontAwesomeIcon icon={faCalendar} />,
  },
];

const learningTaskType = 'learning_task';
const staffFormType = 'staff_form';

export const EL_TABS = TABS.filter(tab => tab.label !== 'Events');

export const API_FUNC_MAP = Object.freeze({
  [ACTIVITIES_TAB_ID]: {
    apiFunc: Api.surveys.getActivityList,
    apiFuncName: 'surveys.getActivityList',
    mapApiFilters: {
      surveyInstanceType: learningTaskType,
    },
    searchField: 'query',
  },
  [STAFF_FORMS_TAB_ID]: {
    apiFunc: Api.surveys.getActivityList,
    apiFuncName: 'surveys.getActivityList',
    mapApiFilters: {
      surveyInstanceType: staffFormType,
    },
    searchField: 'query',
  },
  [LEARNING_TASKS_TAB_ID]: {
    apiFunc: Api.surveys.getLearningTaskList,
    apiFuncName: 'surveys.getLearningTaskList',
    searchField: 'query',
  },
  [EVENTS_TAB_ID]: {
    apiFunc: Api.eventScheduler.getEventsSurveyAnalytics,
    apiFuncName: 'eventScheduler.getEventsSurveyAnalytics',
    searchField: 'search',
  },
});

export const COUNTS_API_FUNC_MAP = Object.freeze({
  [STAFF_FORMS_TAB_ID]: {
    apiFunc: Api.surveys.getActivityListCounts,
    apiFuncName: 'surveys.getActivityListCounts',
    getQueryParams: () => {},
  },
  [ACTIVITIES_TAB_ID]: {
    apiFunc: Api.surveys.getActivityListCounts,
    apiFuncName: 'surveys.getActivityListCounts',
    getQueryParams: () => {},
  },
  [LEARNING_TASKS_TAB_ID]: {
    apiFunc: Api.surveys.getLearningTaskListCounts,
    apiFuncName: 'surveys.getLearningTaskListCounts',
    getQueryParams: items => ({
      chapterId: ensureArray(items).map(item => item.id),
    }),
  },
});
